import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  IExperienceDenormalized,
  IExperienceDenormalizedV2,
  isExperienceDenormalizedV2
} from '@dadohr/shared/domainTypes/experience';
import { IRecipient } from '@dadohr/shared/domainTypes/mail';
import { useRef } from 'react';
import ReactHelmet from 'react-helmet';
import { Loader } from '../../../../../components/Loader';
import { useErrorLogger } from '../../../../../hooks/useErrorLogger';
import { TopContainer } from '../../../../components/TopContainer';
import { TopNav } from '../../../../components/TopNav';
import { MaybeImpersonationBanner } from '../../../../components/ImpersonationBanner';
import { useFullstoryIdentifyForStandaloneApp } from '../../../../hooks/useFullstoryIdentifyForStandaloneApp';
import { useStandaloneAppThemeOverride } from '../../../../hooks/useStandaloneAppContext';
import { sendEmployeeAppErrorAlert } from '../../../../services/alerts';
import { EmployeeAppExperienceInstanceWithData } from '../../../../v2/featutres/Experience/pages/Instance';
import { useEmployeeAppExperienceInstanceV1OrV2 } from '../../../../v2/featutres/Experience/services/retrieval';
import { WrongSsoUserInSession } from '../../components/WrongSsoUserInSession';
import {
  NOT_FOUND_MESSAGES,
  useStackdriverSetup,
  useTaskCompletionSuccessNotifications
} from '../../service';
import { StandaloneAppPageExperienceOverviewInner } from './InnerPage';

export const StandaloneAppPageExperienceOverviewWithData = ({
  experience,
  accessToken
}: {
  experience: Doc<IExperienceDenormalized>;
  accessToken: string;
}) => {
  useStandaloneAppThemeOverride(experience?.data.org.experienceTheming || null);
  useTaskCompletionSuccessNotifications(experience);
  useStackdriverSetup(experience);
  useFullstoryIdentifyForStandaloneApp(experience);

  return (
    <>
      <ReactHelmet>
        <title>{experience.data.title}</title>
      </ReactHelmet>
      <MaybeImpersonationBanner user={experience.data.user} />
      <TopContainer>
        <TopNav
          d={{
            accessToken: experience.data.accessToken,
            org: experience.data.org,
            title: experience.data.title,
            mail: experience.data.contacts?.find((c) => c.type === 'MAIL') as
              | IRecipient
              | undefined
          }}
        />
      </TopContainer>
      <StandaloneAppPageExperienceOverviewInner
        experience={experience}
        showTopBanner
      />
    </>
  );
};

export const StandaloneAppPageExperienceOverview = ({
  accessToken,
  v
}: {
  accessToken: string;
  v: number;
}) => {
  const [experience, loading, error] = useEmployeeAppExperienceInstanceV1OrV2(
    accessToken,
    v
  );
  useErrorLogger('standaloneExperience get', error);

  const intermediaryErrorSent = useRef(false);

  // The model is updated e.g. when sub tasks complete. Through this mechanic
  // we gather an updated state of the experience - but there's no need to show
  // a loader for it! It can just update in place.
  if (loading && !experience) {
    return <Loader />;
  }

  if (error?.status === 'SSO_WRONG_USER_IN_SESSION') {
    return (
      <WrongSsoUserInSession
        email={error.sessionEmail}
        provider={error.ssoProvider}
      />
    );
  }

  if (NOT_FOUND_MESSAGES.has(error?.message)) {
    throw new Error('Not found');
  }

  if (!experience) {
    if (!intermediaryErrorSent.current) {
      intermediaryErrorSent.current = true;
      sendEmployeeAppErrorAlert({
        url: window?.location?.href || '',
        error: 'No experience - most likely intermittent rendering state',
        info: new Date().toISOString(),
        userAgent: window.navigator.userAgent,
        accessToken
      });
    }
    return null;
    // throw new Error('Something went wrong');
  }

  if (intermediaryErrorSent.current) {
    sendEmployeeAppErrorAlert({
      url: window?.location?.href || '',
      error: 'Intermittent error resolved itself immediately',
      info: new Date().toISOString(),
      userAgent: window.navigator.userAgent,
      accessToken
    });
    intermediaryErrorSent.current = false;
  }

  if (isExperienceDenormalizedV2(experience.data)) {
    return (
      <EmployeeAppExperienceInstanceWithData
        instance={experience as Doc<IExperienceDenormalizedV2>}
        actorAccessToken=""
        instanceAccessToken={accessToken}
      />
    );
  } else {
    return (
      <StandaloneAppPageExperienceOverviewWithData
        accessToken={accessToken}
        experience={experience as Doc<IExperienceDenormalized>}
      />
    );
  }
};
